import React, { useEffect, useRef, useState } from 'react';
import { H2, Tag, Intent, Callout, Spinner } from '@blueprintjs/core';
import EloHistoryChart from '../components/EloHistoryChart';
import { subscribeToActHistory } from '../firebase/actService';
import { getCurrentEloRatings, recalculateEloFromHistory } from '../firebase/eloHistoricalCalculator';
import { getPlayersWithEloRatings, getAllPlayerStatuses, subscribeToPlayerStatuses } from '../firebase/eloManagement';
import { fetchMultipleEloHistories } from '../firebase/eloService';

function EloHistory() {
  const [history, setHistory] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [eloHistories, setEloHistories] = useState({});
  const [firebaseEloHistories, setFirebaseEloHistories] = useState({});
  const [playerStatuses, setPlayerStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const loadSeqRef = useRef(0);

  useEffect(() => {
    const unsubscribeStatuses = subscribeToPlayerStatuses((statuses) => {
      setPlayerStatuses(statuses || {});
    });

    getAllPlayerStatuses()
      .then((statuses) => setPlayerStatuses(statuses || {}))
      .catch((err) => {
        console.error('Error loading player statuses for ELO history page:', err);
      });

    return () => {
      if (unsubscribeStatuses) unsubscribeStatuses();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const findHistoryKey = (histories, candidates) => {
      const validCandidates = candidates.filter(Boolean);
      for (const candidate of validCandidates) {
        if (histories[candidate]) return candidate;
      }

      const historyKeys = Object.keys(histories);
      for (const candidate of validCandidates) {
        const lowerCandidate = candidate.toLowerCase().trim();
        const matched = historyKeys.find(key => key.toLowerCase().trim() === lowerCandidate);
        if (matched) return matched;
      }

      return null;
    };

    const buildChartData = async (actHistory, seq) => {
      if (!isMounted) return;

      setHistory(actHistory);
      setError('');

      if (actHistory.length === 0) {
        if (!isMounted || seq !== loadSeqRef.current) return;
        setPlayerStats([]);
        setEloHistories({});
        setFirebaseEloHistories({});
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const [currentEloRatings, playersWithEloMap] = await Promise.all([
          getCurrentEloRatings(),
          getPlayersWithEloRatings(actHistory)
        ]);

        const { histories: rawCalculatedHistories } = await recalculateEloFromHistory(actHistory, currentEloRatings);

        if (!isMounted || seq !== loadSeqRef.current) return;

        const players = Object.values(playersWithEloMap);
        const calculatedHistoriesByName = {};

        players.forEach((player) => {
          const historyKey = findHistoryKey(rawCalculatedHistories, [player.name, player.eloKey]);
          if (historyKey && rawCalculatedHistories[historyKey]) {
            calculatedHistoriesByName[player.name] = rawCalculatedHistories[historyKey];
          }
        });

        const playersForChart = players
          .map((player) => {
            const calculatedHistory = calculatedHistoriesByName[player.name] || [];
            const currentElo =
              player.elo ??
              (player.eloKey && currentEloRatings[player.eloKey] !== undefined ? currentEloRatings[player.eloKey] : null) ??
              (currentEloRatings[player.name] !== undefined ? currentEloRatings[player.name] : null) ??
              (calculatedHistory.length > 0 ? calculatedHistory[calculatedHistory.length - 1].elo : null);

            return {
              name: player.name,
              displayName: player.name,
              elo: currentElo !== null && currentElo !== undefined ? Math.round(currentElo) : null,
              hasEloRating: currentElo !== null && currentElo !== undefined,
              eloHistory: calculatedHistory
            };
          })
          .sort((a, b) => {
            if (a.elo === null && b.elo === null) return a.name.localeCompare(b.name);
            if (a.elo === null) return 1;
            if (b.elo === null) return -1;
            return b.elo - a.elo;
          });

        setPlayerStats(playersForChart);
        setEloHistories(calculatedHistoriesByName);

        const firebaseHistoryTargets = playersForChart
          .filter((player) => player.hasEloRating)
          .map((player) => {
            const source = playersWithEloMap[player.name];
            return {
              name: player.name,
              key: source?.eloKey || player.name
            };
          });

        const uniqueFirebaseKeys = [...new Set(firebaseHistoryTargets.map((t) => t.key).filter(Boolean))];
        const rawFirebaseHistories = uniqueFirebaseKeys.length > 0
          ? await fetchMultipleEloHistories(uniqueFirebaseKeys)
          : {};

        if (!isMounted || seq !== loadSeqRef.current) return;

        const firebaseHistoriesByName = {};
        firebaseHistoryTargets.forEach(({ name, key }) => {
          if (rawFirebaseHistories[key]) {
            firebaseHistoriesByName[name] = rawFirebaseHistories[key];
          }
        });

        setFirebaseEloHistories(firebaseHistoriesByName);
      } catch (err) {
        console.error('Error loading ELO history page data:', err);
        if (!isMounted || seq !== loadSeqRef.current) return;
        setError(err?.message || 'Failed to load ELO history data.');
        setPlayerStats([]);
        setEloHistories({});
        setFirebaseEloHistories({});
      } finally {
        if (isMounted && seq === loadSeqRef.current) {
          setIsLoading(false);
        }
      }
    };

    const unsubscribe = subscribeToActHistory((actHistory) => {
      const seq = ++loadSeqRef.current;
      void buildChartData(actHistory, seq);
    });

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  if (isLoading && history.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner />
          <div style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading ELO history graph...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '1.5rem' }}>
        <H2 style={{ marginTop: 0 }}>ELO History</H2>
        <Callout intent={Intent.DANGER} title="Failed to Load ELO History">
          {error}
        </Callout>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 'min(640px, 90vw)' }}>
          <H2 style={{ margin: '0 0 1rem 0' }}>ELO History</H2>
          <Callout intent={Intent.PRIMARY} title="No Data Available">
            Complete some ACT scorecards to generate ELO history graphs.
          </Callout>
        </div>
      </div>
    );
  }

  const playersWithAnyHistory = playerStats.filter((player) =>
    (player.eloHistory && player.eloHistory.length > 0) ||
    (firebaseEloHistories[player.name] && firebaseEloHistories[player.name].length > 0)
  );

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <H2 style={{ margin: 0 }}>ELO History</H2>
        <Tag intent={Intent.PRIMARY}>{history.length} ACT{history.length !== 1 ? 's' : ''}</Tag>
        <Tag minimal>{playersWithAnyHistory.length} chartable players</Tag>
      </div>

      <EloHistoryChart
        playerStats={playerStats}
        history={history}
        eloHistories={eloHistories}
        firebaseEloHistories={firebaseEloHistories}
        playerStatuses={playerStatuses}
      />
    </div>
  );
}

export default EloHistory;
